<template>
	<div class="ImageAspectRatio" :class="classes">
		<div class="AspectRatioContent">
			<!-- @slot img element should go here -->
			<slot name="image" />
		</div>
	</div>
</template>

<script>
export default {
	components: {},
	data() {
		return {};
	},
	props: {
		/**
		 * square, circle, fourByThree, golden, sixteenByNine, twentyoneByNine
		 */
		shape: {
			type: String,
			default: null,
		},
		/**
		 * reverse
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * rounded
		 */
		rounded: {
			type: Boolean,
			default: false,
		},
		/**
		 * contain
		 */
		contain: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		classes() {
			return {
				'ImageAspectRatio--reverse': this.reverse,
				'ImageAspectRatio--rounded': this.rounded,
				'ImageAspectRatio--contain': this.contain,
				'ImageAspectRatio--transparent': this.transparent,
				'ImageAspectRatio--circle': this.shape === 'circle',
				'ImageAspectRatio--golden': this.shape === 'golden',
				'ImageAspectRatio--fourByThree': this.shape === 'fourByThree',
				'ImageAspectRatio--fiveByThree': this.shape === 'fiveByThree',
				'ImageAspectRatio--sixByFour': this.shape === 'sixByFour',
				'ImageAspectRatio--sixteenByNine': this.shape === 'sixteenByNine',
				'ImageAspectRatio--twentyOneByNine': this.shape === 'twentyOneByNine',
				'ImageAspectRatio--hero': this.shape === 'hero',
				'ImageAspectRatio--heroWide': this.shape === 'heroWide',
				'ImageAspectRatio--heroSquareToWide': this.shape === 'heroSquareToWide',
			};
		},
	},
};
</script>

<style lang="scss">
.ImageAspectRatio {
	@include aspect-ratio(1, 1); /* Default is a perfect square */
	max-width: 1200px; /* Never exceed maximum ML size to support retina */
	width: 100%;
	overflow: hidden;
	/* Mandatory child class */
	.AspectRatioContent {
		justify-content: center;
		display: flex;
		background-color: $color-neutral-0;
		img {
			height: 100%;
			max-width: 100%;
			@supports (display: grid) {
				object-fit: cover;
				width: 100%;
			}
		}
	}
}

/* --------------------------------
	ImageAspectRatio variants
-------------------------------- */

/* Circle - for circular containers */
.ImageAspectRatio--circle {
	.AspectRatioContent {
		background-color: transparent;
		img {
			border-radius: 50%;
		}
	}
}

/* 4:3 - standard DSLR output */
.ImageAspectRatio--fourByThree {
	@include aspect-ratio(4, 3);
}

/* 5:3 - standard DSLR output */
.ImageAspectRatio--fiveByThree {
	@include aspect-ratio(5, 3);
}

/* 6:4 - standard photo print */
.ImageAspectRatio--sixByFour {
	@include aspect-ratio(6, 4);
}

/* GOLDEN RATIO - the perfect rectangle based on the fibonacci sequence */
.ImageAspectRatio--golden {
	@include aspect-ratio(1.618, 1);
}

/* 16:9 - standard video and laptop screen size */
.ImageAspectRatio--sixteenByNine {
	@include aspect-ratio(16, 9);
}

/* 21:9 - widescreen cinema format (similar to CinemaScope or anamorphic) */
.ImageAspectRatio--twentyOneByNine {
	@include aspect-ratio(21, 9);
}

/* Hero gets wider for larger screens where height may be an issue */
.ImageAspectRatio--hero {
	@include aspect-ratio(1.618, 1);
	@include breakpoint(xlarge) {
		@include aspect-ratio(16, 9);
	}
}

/* Hero gets wider for larger screens where height may be an issue */
.ImageAspectRatio--heroWide {
	@include aspect-ratio(16, 9);
	@include breakpoint(xlarge) {
		@include aspect-ratio(21, 9);
	}
}

/* Hero starts more squarish then gets wider for larger screens where height may be an issue */
.ImageAspectRatio--heroSquareToWide {
	@include aspect-ratio(4, 3);
	@include breakpoint(medium) {
		@include aspect-ratio(1.618, 1);
	}
	@include breakpoint(large) {
		@include aspect-ratio(16, 9);
	}
	@include breakpoint(xlarge) {
		@include aspect-ratio(21, 9);
	}
}

/* Rounded variant - adds subtle rounded corners */
.ImageAspectRatio--rounded {
	border-radius: $radius-sm;
}

/* Contain variant - never crops, vertically and horizontally centered if image is smaller than container */
.ImageAspectRatio--contain {
	.AspectRatioContent {
		// override default cropping behavior
		overflow: visible;
		// override flex
		display: block;
		img {
			max-height: 100%;
			max-width: 100%;
			// need to override the default style of 100% width and height
			width: auto;
			height: auto;
			// center vertically and horizontally
			@include centerCenter;
		}
	}
}

/* Very wide at large and above */
.ImageAspectRatio--heroFullWidth {
	max-width: none;
	@include aspect-ratio(16, 9);
	@include breakpoint(large) {
		@include aspect-ratio(22, 9);
	}
}

/* Reverse variant - dark background */
.ImageAspectRatio--reverse {
	.AspectRatioContent {
		background-color: $color-neutral-5;
	}
}

/* Transparent background */
.ImageAspectRatio--transparent {
	.AspectRatioContent {
		background-color: transparent;
	}
}
</style>
